/**
 * Wave F — post-kiosk land staging.
 * Active tenant always (guest only); signed-in records contact when resolve
 * returns concrete salesPointId. Login resume to post-kiosk re-runs when session
 * appears. Stage 6: no silent favorite on land — post-purchase prompt handles favorites.
 */

import { useEffect, useRef } from 'react';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { tryStageGuestActiveTenantVisit } from '../account/pendingActiveTenants.js';
import { recordSalesPointContactSilent } from '../account/salesPointContactsApi.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import type { PostKioskTokenResolution } from './checkoutApi.js';

export function usePostKioskGuestLandStaging(
  resolution: PostKioskTokenResolution | null | undefined,
): void {
  const tenantCode = useTenantCode();
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const stagedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (resolution === null || resolution === undefined) {
      return;
    }

    const signedIn = isGloballySignedInChrome({ session });
    const salesPointId =
      typeof resolution.salesPointId === 'number' && resolution.salesPointId > 0
        ? resolution.salesPointId
        : null;
    const stageKey = `${tenantCode.trim().toLowerCase()}:${salesPointId ?? 'none'}:${signedIn ? 'in' : 'out'}`;
    if (stagedKeyRef.current === stageKey) {
      return;
    }
    stagedKeyRef.current = stageKey;

    tryStageGuestActiveTenantVisit({
      tenantCode,
      hasAccessToken: signedIn,
    });

    if (salesPointId === null) {
      return;
    }

    if (signedIn) {
      void recordSalesPointContactSilent({
        apiBaseUrl,
        body: { salesPointId },
      }).catch(() => {
        /* transport — land path stays silent; MEMBERSHIP_BLOCKED already swallowed */
      });
      return;
    }
  }, [apiBaseUrl, resolution, session, tenantCode]);
}
